import React, { useState } from 'react';
import MetricCard from './MetricCard';
import Panel from './Panel';
import DonutBreakdown from './charts/DonutBreakdown';
import StackedTrendChart from './charts/StackedTrendChart';
import DataTable from './DataTable';
import { productionIncident as data } from './mockData';
import { TIER_COLORS, PI_PRIORITY_SERIES, ORIGIN_SERIES } from './chartColors';
import useProductionIncidentBreakdown from '../../hooks/useProductionIncidentBreakdown';
import useProductionIncidentByOrigin from '../../hooks/useProductionIncidentByOrigin';
import useProductionIncidentList from '../../hooks/useProductionIncidentList';
import useProductionIncidentSummary from '../../hooks/useProductionIncidentSummary';
import { JIRA_BASE } from './constants';

const ORIGIN_VIEWS = [
  { key: 'week', label: 'Week over Week' },
  { key: 'month', label: 'Month over Month' },
  { key: 'unit', label: 'Product Unit' },
  { key: 'priority', label: 'Priority' },
  { key: 'component', label: 'Product Component' },
];

const DETAIL_COLUMNS = [
  { key: 'summary', label: 'Summary', render: r => <a href={`${JIRA_BASE}/${r.key}`} target="_blank" rel="noopener noreferrer">[{r.key}] {r.summary}</a> },
  { key: 'created', label: 'Reported On', render: r => r.created ? new Date(r.created).toLocaleDateString() : '—' },
  { key: 'reporter', label: 'Reporter' },
  { key: 'identifiedBy', label: 'Identified By' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'escalationTrigger', label: 'Escalation Trigger' },
  { key: 'labels', label: 'Labels' },
];

function ProductionIncidentTab({ filters, dateRange }) {
  const [originView, setOriginView] = useState('week');

  // Fetch summary data using the new hook
  const { data: summaryData, loading: summaryLoading, error: summaryError } = useProductionIncidentSummary(filters, dateRange);

  // Fetch breakdown data for priority, identified-by, and priority-monthly
  const { data: priorityData } = useProductionIncidentBreakdown('priority', filters, dateRange);
  const { data: identifiedByData } = useProductionIncidentBreakdown('identified-by', filters, dateRange);
  const { data: priorityMonthlyData } = useProductionIncidentBreakdown('priority-monthly', filters, dateRange);

  // Fetch by-origin data for all breakdown types
  const { data: byOriginWeekData } = useProductionIncidentByOrigin('week', filters, dateRange);
  const { data: byOriginMonthData } = useProductionIncidentByOrigin('month', filters, dateRange);
  const { data: byOriginUnitData } = useProductionIncidentByOrigin('unit', filters, dateRange);
  const { data: byOriginPriorityData } = useProductionIncidentByOrigin('priority', filters, dateRange);
  const { data: byOriginComponentData } = useProductionIncidentByOrigin('component', filters, dateRange);

  // Detailed View table — real data from the API, same minimal (no pagination/sort UI yet)
  // pattern as useServiceDisruptionOpen/useSupportBugsOpen on the Quality Overview tab.
  const { data: incidentListData, error: incidentListError } = useProductionIncidentList(filters, dateRange);
  const incidentListRows = incidentListData?.items ?? [];

  const currentCount = summaryData?.current?.count;
  const previousCount = summaryData?.previous?.count;
  const changePct = previousCount > 0
    ? Math.round(((currentCount - previousCount) / previousCount) * 1000) / 10
    : null;

  const comparisonText = summaryData
    ? `Previous period: ${previousCount} incidents (${summaryData.previous.from} to ${summaryData.previous.to})`
    : null;

  // Transform priority-monthly data for StackedTrendChart
  // Backend returns: [{ month: '2026-01', priority: 'P1 - Critical', count: 1 }, ...]
  // StackedTrendChart expects: [{ month: '2026-01', p1Critical: 1, p2Major: 2, ... }, ...]
  const transformPriorityMonthly = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    const monthMap = {};
    data.forEach(row => {
      const month = row.month;
      if (!monthMap[month]) {
        monthMap[month] = { month };
      }
      
      // Map priority to field name (handle null/undefined)
      let fieldName = 'null';
      if (row.priority) {
        // Priority format: "P1 - Critical", "P2 - Major", "P3 - Medium", "P4 - Low"
        const priorityLower = row.priority.toLowerCase();
        if (priorityLower.includes('p1') || priorityLower.includes('critical')) {
          fieldName = 'p1Critical';
        } else if (priorityLower.includes('p2') || priorityLower.includes('major')) {
          fieldName = 'p2Major';
        } else if (priorityLower.includes('p3') || priorityLower.includes('medium')) {
          fieldName = 'p3Medium';
        } else if (priorityLower.includes('p4') || priorityLower.includes('low')) {
          fieldName = 'p4Low';
        }
      }
      
      monthMap[month][fieldName] = row.count;
    });
    
    return Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
  };

  const transformedPriorityMonthly = transformPriorityMonthly(priorityMonthlyData);

  // Transform by-origin data from backend format to chart format
  // Backend returns: [{ origin: 'CS', bucket: 'bucket-value', count: 1 }, ...]
  // Chart expects: [{ bucket: 'bucket-value', cs: 1, hj: 0, heap: 0 }, ...]
  const transformByOriginData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    const bucketMap = {};
    data.forEach(row => {
      const bucket = row.bucket;
      if (!bucketMap[bucket]) {
        bucketMap[bucket] = { bucket, cs: 0, hj: 0, heap: 0 };
      }
      
      const origin = row.origin?.toLowerCase();
      if (origin === 'cs') {
        bucketMap[bucket].cs += row.count;
      } else if (origin === 'hj') {
        bucketMap[bucket].hj += row.count;
      } else if (origin === 'heap') {
        bucketMap[bucket].heap += row.count;
      }
    });
    
    return Object.values(bucketMap);
  };

  // Map API data for each breakdown type (to check if it's been loaded)
  const byOriginApiData = {
    week: byOriginWeekData,
    month: byOriginMonthData,
    unit: byOriginUnitData,
    priority: byOriginPriorityData,
    component: byOriginComponentData,
  };

  // Transform data for each breakdown type
  const byOriginDataByView = {
    week: transformByOriginData(byOriginWeekData),
    month: transformByOriginData(byOriginMonthData),
    unit: transformByOriginData(byOriginUnitData),
    priority: transformByOriginData(byOriginPriorityData),
    component: transformByOriginData(byOriginComponentData),
  };

  return (
    <div className="qk-tab-content">
      <Panel accent="purple">
        {summaryError && <div className="section-error">Couldn't load Production Incident summary.</div>}
        <div className="qk-metric-group-cards">
          <MetricCard
            label="#Production Incident"
            value={summaryLoading ? 'Loading…' : currentCount ?? '—'}
            change={changePct}
            changeLabel="from previous period"
            comparisonText={comparisonText}
          />
        </div>
        <div className="qk-chart-row">
          <div className="qk-chart-col">
            <div className="qk-chart-title">Production Incident by Priority</div>
            <DonutBreakdown data={priorityData || data.byPriority} colors={Object.values(TIER_COLORS)} />
          </div>
          <div className="qk-chart-col">
            <div className="qk-chart-title">Production Incident by Identified By</div>
            <DonutBreakdown data={identifiedByData || data.byIdentifiedBy} />
          </div>
        </div>
      </Panel>

      <Panel title="Production Incident - by Priority - Month Over Month" accent="purple">
        <StackedTrendChart data={priorityMonthlyData !== null ? transformedPriorityMonthly : data.priorityMonthly} dimensionKey="month" series={PI_PRIORITY_SERIES} />
      </Panel>

      <Panel title="Production Incident Detailed View" accent="purple">
        {incidentListError && <div className="section-error">Couldn’t load Production Incident details.</div>}
        <DataTable
          columns={DETAIL_COLUMNS}
          rows={incidentListRows}
          ariaLabel="Production Incident Detailed View"
        />
      </Panel>

      <Panel title="Production Incident - By Origin CS/HJ/Heap" accent="purple">
        <div className="qk-view-toggle">
          {ORIGIN_VIEWS.map(v => (
            <button
              key={v.key}
              className={`qk-view-toggle-btn${originView === v.key ? ' active' : ''}`}
              onClick={() => setOriginView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <StackedTrendChart
          data={byOriginApiData[originView] !== null ? byOriginDataByView[originView] : data.byOrigin[originView]}
          dimensionKey="bucket"
          series={ORIGIN_SERIES}
        />
      </Panel>
    </div>
  );
}

export default ProductionIncidentTab;
